import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../endpoints';
import { FileWithUrl } from '../documents';
import { Site } from '../planning';
import { User } from '../users';
import FormData from 'form-data';
import { GnrSupplier } from './gnrSuppliers';
import { GnrUsage } from './gnrUsages';
import { GnrUtilityVehicleUsage } from './gnrUtilityVehicleUsages';

export interface GnrRequest {
    _id: string;
    requestVolume: number;
    siteId: Site;
    requesterId: User;
    requestDate: string;
    requestPeriod: number;
    plannedPeriod?: number;
    plannedDate?: string;
    plannerId?: User;
    plannedVolume?: number;
    deliveryDate?: string;
    deliveryReceiptPhotoId?: string;
    deliveryReceiptPhotoFile?: FileWithUrl;
    receiverId?: User;
    receivedVolume?: number;
    state: number;
    gnrSupplierId?: GnrSupplier;
    createdAt: string;
    receivedObservations?: string;
    gnrUsages?: GnrUsage[];
    gnrUtilityVehicleUsages?: GnrUtilityVehicleUsage[];
}

export interface GnrRequestResponse {
    data: GnrRequest;
    message: string;
}

export interface GnrRequestsListResponse {
    data: GnrRequest[];
    message: string;
}

export interface GetAllGnrRequestsQueries {
    states?: number[];
    includeFiles?: boolean;
    includeGnrUsages?: boolean;
}

export const getAllGnrRequests = async (queries: GetAllGnrRequestsQueries = {}): Promise<GnrRequestsListResponse> => {
    try {
        const response: AxiosResponse<GnrRequestsListResponse> = await apiClient.get('/gnr-requests', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface GetGnrRequestByIdQueries {
    includeFiles?: boolean;
    includeGnrUsages?: boolean;
}

export const getGnrRequestById = async (id: string, queries: GetGnrRequestByIdQueries = {}): Promise<GnrRequestResponse> => {
    try {
        const response: AxiosResponse<GnrRequestResponse> = await apiClient.get(`/gnr-requests/${id}`, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateGnrRequestBody {
    requestVolume: number;
    siteId: string;
    requesterId: string;
    requestDate: Date;
    requestPeriod: number;
    plannedPeriod?: number;
    plannedDate?: Date;
    plannerId?: string;
    plannedVolume?: number;
    deliveryDate?: Date;
    receiverId?: string;
    receivedVolume?: number;
    state: number;
    receivedObservations?: string;
}

export interface CreateGnrRequestQueries {
    withNotification?: boolean;
}

export const createGnrRequest = async (data: CreateGnrRequestBody, queries?: CreateGnrRequestQueries): Promise<GnrRequestResponse> => {
    try {
        const response: AxiosResponse<GnrRequestResponse> = await apiClient.post('/gnr-requests', data, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateGnrRequestBody {
    requestVolume?: number;
    siteId?: string;
    requesterId?: string;
    requestDate?: Date;
    requestPeriod?: number;
    plannedPeriod?: number;
    plannedDate?: Date;
    plannerId?: string;
    plannedVolume?: number;
    deliveryDate?: Date;
    deliveryReceiptPhotoId?: string;
    receiverId?: string;
    receivedVolume?: number;
    state?: number;
    gnrSupplierId?: string;
    receivedObservations?: string;
}

export const updateGnrRequest = async (id: string, body: UpdateGnrRequestBody, file?: File): Promise<GnrRequestResponse> => {
    try {
        const formData = new FormData();
        Object.entries(body).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value.toString());
            }
        });
        if (file) {
            formData.append('deliveryReceiptPhoto', file);
        }
        const response: AxiosResponse<GnrRequestResponse> = await apiClient.patch(`/gnr-requests/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface BulkUpdateGnrRequestBody {
    gnrRequestIds: string[];
    supplierId: string;
}

export interface BulkUpdateGnrRequestQueries {
    withNotification?: boolean;
}

export const bulkUpdateGnrRequests = async (
    body: BulkUpdateGnrRequestBody,
    queries?: BulkUpdateGnrRequestQueries
): Promise<{ message: string; modifiedCount: number }> => {
    try {
        const response: AxiosResponse<{ message: string; modifiedCount: number }> = await apiClient.patch('/gnr-requests', body, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const archiveGnrRequest = async (id: string): Promise<GnrRequestResponse> => {
    try {
        const response: AxiosResponse<GnrRequestResponse> = await apiClient.patch(`/gnr-requests/${id}/archive`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface ValidateDeliveryGnrRequestBody {
    deliveryDate: Date;
    receiverId: string;
    receivedVolume: number;
    receivedObservations?: string;
}

export interface ValidateDeliveryGnrRequestQuery {
    withNotification?: boolean;
}

export const validateDeliveryGnrRequest = async (
    gnrRequestId: string,
    body: ValidateDeliveryGnrRequestBody,
    queries?: ValidateDeliveryGnrRequestQuery,
    file?: File
): Promise<GnrRequestResponse> => {
    try {
        const formData = new FormData();
        Object.entries(body).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value.toString());
            }
        });
        if (file) {
            formData.append('deliveryReceiptPhoto', file);
        }
        const response: AxiosResponse<GnrRequestResponse> = await apiClient.patch(`/gnr-requests/delivery/validate/${gnrRequestId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: queries,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
